import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { format } from 'date-fns';
import { emailService, EmailDetail as EmailDetailType } from '../services/api';
import './EmailDetail.css';

const EmailDetail: React.FC = () => {
  const { messageId } = useParams<{ messageId: string }>();
  const navigate = useNavigate();
  const [email, setEmail] = useState<EmailDetailType | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [viewMode, setViewMode] = useState<'html' | 'text'>('html');

  useEffect(() => {
    const fetchEmail = async () => {
      if (!messageId) return;
      
      setLoading(true);
      setError(null);
      try {
        const data = await emailService.getEmail(messageId);
        setEmail(data);
        // Set default view mode based on available content
        if (data.html) {
          setViewMode('html');
        } else {
          setViewMode('text');
        }
      } catch (err: any) {
        setError(err.response?.data?.error || 'Failed to fetch email');
        console.error('Error fetching email:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchEmail();
  }, [messageId]);

  if (loading) {
    return <div className="loading">Loading email...</div>;
  }

  if (error) {
    return (
      <div className="error-container">
        <div className="error-message">{error}</div>
        <button onClick={() => navigate('/')} className="back-button">
          Back to List
        </button>
      </div>
    );
  }

  if (!email) {
    return <div className="empty-state">Email not found</div>;
  }

  return (
    <div className="email-detail-container">
      <button onClick={() => navigate('/')} className="back-button">
        ← Back to List
      </button>

      <div className="email-detail-card">
        <div className="email-detail-header">
          <div className="email-detail-from">
            <strong>From:</strong> {email.from}
          </div>
          <div className="email-detail-to">
            <strong>To:</strong> {email.to}
          </div>
          <div className="email-detail-date">
            {format(new Date(email.timestamp), 'MMMM d, yyyy HH:mm:ss')}
          </div>
        </div>

        <div className="email-detail-subject">
          {email.subject || '(No Subject)'}
        </div>

        {email.attachments && email.attachments.length > 0 && (
          <div className="email-attachments">
            <strong>Attachments:</strong>
            <ul>
              {email.attachments.map((attachment, index) => (
                <li key={index}>
                  {attachment.filename} ({attachment.size} bytes, {attachment.contentType})
                </li>
              ))}
            </ul>
          </div>
        )}

        {email.html && email.text && (
          <div className="view-mode-toggle">
            <button
              onClick={() => setViewMode('html')}
              className={viewMode === 'html' ? 'active' : ''}
            >
              HTML
            </button>
            <button
              onClick={() => setViewMode('text')}
              className={viewMode === 'text' ? 'active' : ''}
            >
              Text
            </button>
          </div>
        )}

        <div className="email-detail-content">
          {viewMode === 'html' && email.html ? (
            <div
              className="email-html-content"
              dangerouslySetInnerHTML={{ __html: email.html }}
            />
          ) : (
            <div className="email-text-content">
              {email.text || '(No content)'}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default EmailDetail;

